/**
 * Çalışma Logları sekmesi.
 *
 * Server-side pagination ile çalışır (gts_reports_api: /api/ReportRunLogs/list-paged).
 * Büyük log sayılarında (binlerce kayıt) bile akıcı çalışır.
 *
 * Detay satırında alıcı listesi, SMTP host, SMTP yanıtı ve MessageId gibi
 * teslim takip bilgileri gösterilir — kullanıcı mailin gerçekten gönderilip
 * gönderilmediğini SMTP'nin döndüğü yanıttan teyit edebilir.
 */

import React from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import HistoryIcon from '@mui/icons-material/History';
import InboxIcon from '@mui/icons-material/Inbox';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import DnsIcon from '@mui/icons-material/Dns';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import { useAuth } from '../../../../contexts/AuthContext';
import { listRunLogsPaged } from '../services/mailReportsApi';
import type { ReportRunLog } from '../types';
import { formatLocalDateTime } from '../utils/dateFormat';

type StatusFilter = 'all' | 'success' | 'failed' | 'skipped';

const RunLogsTab: React.FC = () => {
  const { getBranchName } = useAuth();
  const [logs, setLogs] = React.useState<ReportRunLog[]>([]);
  const [totalCount, setTotalCount] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [expandedId, setExpandedId] = React.useState<number | null>(null);

  // Pagination state — TablePagination 0-bazlı, backend 1-bazlı
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>('all');

  const reload = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listRunLogsPaged({
        page: page + 1,
        pageSize: rowsPerPage,
        status: statusFilter === 'all' ? null : statusFilter,
      });
      setLogs(res.Items);
      setTotalCount(res.TotalCount);
    } catch (err: any) {
      setError(err?.response?.data?.Message || err?.message || 'Loglar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter]);

  React.useEffect(() => {
    reload();
  }, [reload]);

  // Filter değişince sayfa 0'a dön
  const handleStatusFilterChange = (
    _: React.MouseEvent<HTMLElement>,
    newFilter: StatusFilter | null,
  ) => {
    if (newFilter) {
      setStatusFilter(newFilter);
      setPage(0);
    }
  };

  // RunLog'da schedule TZ saklanmadığı için TR varsayılır (mevcut davranış).
  const fmtDateTime = (iso: string): string => formatLocalDateTime(iso, 'Turkey Standard Time');

  const statusChip = (status: string) => {
    if (status === 'success')
      return (
        <Chip
          size="small"
          icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
          label="Başarılı"
          color="success"
          sx={{ fontWeight: 600 }}
        />
      );
    if (status === 'failed')
      return (
        <Chip
          size="small"
          icon={<ErrorOutlineIcon sx={{ fontSize: 14 }} />}
          label="Başarısız"
          color="error"
          sx={{ fontWeight: 600 }}
        />
      );
    return (
      <Chip
        size="small"
        icon={<RemoveCircleOutlineIcon sx={{ fontSize: 14 }} />}
        label="Atlandı"
        color="default"
        variant="outlined"
      />
    );
  };

  const reportTypeLabel = (t: string) =>
    t === 'weekly' ? 'Haftalık' : t === 'monthly' ? 'Aylık' : 'Günlük';

  const sourceLabel = (s: string) => {
    if (s === 'test-send') return 'Test';
    if (s === 'manual') return 'Manuel';
    return 'Zamanlı';
  };

  const fmtCurrency = (n: number | null): string => {
    if (n == null) return '-';
    return n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' TL';
  };

  // Bu sayfada görünen kayıtlar arasından KPI hesabı
  // (Toplam = TotalCount, Başarılı/Başarısız sayfaya göre — ön-tahmini gösterim).
  const pageStats = React.useMemo(() => {
    const success = logs.filter((l) => l.Status === 'success').length;
    const failed = logs.filter((l) => l.Status === 'failed').length;
    const skipped = logs.filter((l) => l.Status === 'skipped').length;
    return { success, failed, skipped };
  }, [logs]);

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={600}>Çalışma Geçmişi</Typography>
          <Typography variant="caption" color="text.secondary">
            Otomatik mail joblarının sonuçlarını ve teslim detaylarını buradan izleyebilirsiniz.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Yenile">
            <span>
              <IconButton size="small" onClick={reload} disabled={loading}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3}>
          <StatCard
            icon={<HistoryIcon sx={{ color: '#4f46e5' }} />}
            label="Toplam Çalışma"
            value={totalCount}
            tint="rgba(79,70,229,0.08)"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            icon={<CheckCircleIcon sx={{ color: '#16a34a' }} />}
            label={`Başarılı (sayfada)`}
            value={pageStats.success}
            tint="rgba(22,163,74,0.08)"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            icon={<ErrorOutlineIcon sx={{ color: '#dc2626' }} />}
            label={`Başarısız (sayfada)`}
            value={pageStats.failed}
            tint="rgba(220,38,38,0.08)"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            icon={<RemoveCircleOutlineIcon sx={{ color: '#6b7280' }} />}
            label={`Atlandı (sayfada)`}
            value={pageStats.skipped}
            tint="rgba(107,114,128,0.08)"
          />
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
        <ToggleButtonGroup
          value={statusFilter}
          exclusive
          size="small"
          onChange={handleStatusFilterChange}
        >
          <ToggleButton value="all" sx={{ textTransform: 'none' }}>Tümü</ToggleButton>
          <ToggleButton value="success" sx={{ textTransform: 'none' }}>Başarılı</ToggleButton>
          <ToggleButton value="failed" sx={{ textTransform: 'none' }}>Başarısız</ToggleButton>
          <ToggleButton value="skipped" sx={{ textTransform: 'none' }}>Atlandı</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: '#f9fafb' }}>
              <TableCell width={32} />
              <TableCell sx={{ fontWeight: 600 }}>Tarih</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Zamanlama</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Tip</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Şube</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Kaynak</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Durum</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Süre</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Alıcı</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Ciro</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>İşlem</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                  <InboxIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                  <Typography variant="body2">
                    {statusFilter === 'all'
                      ? 'Henüz çalışma logu yok.'
                      : 'Bu filtre için gösterilecek log yok.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => {
                const hasDetail =
                  !!log.ErrorMessage ||
                  !!log.Subject ||
                  !!log.SmtpResponse ||
                  !!log.MessageId ||
                  (log.Recipients && log.Recipients.length > 0) ||
                  (log.CcRecipients && log.CcRecipients.length > 0);
                const isExpanded = expandedId === log.Id;
                return (
                  <React.Fragment key={log.Id}>
                    <TableRow hover>
                      <TableCell>
                        {hasDetail && (
                          <IconButton
                            size="small"
                            onClick={() => setExpandedId(isExpanded ? null : log.Id)}
                          >
                            {isExpanded ? (
                              <KeyboardArrowUpIcon fontSize="small" />
                            ) : (
                              <KeyboardArrowDownIcon fontSize="small" />
                            )}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{fmtDateTime(log.ExecutedAt)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {log.ScheduleId ? `#${log.ScheduleId}` : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>{reportTypeLabel(log.ReportType)}</TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {log.BranchId ? getBranchName(log.BranchId) || log.BranchId : 'Tüm Şubeler'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={sourceLabel(log.TriggerSource)} variant="outlined" />
                      </TableCell>
                      <TableCell>{statusChip(log.Status)}</TableCell>
                      <TableCell align="right">
                        <Typography variant="caption">{log.DurationMs} ms</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption">{log.RecipientCount}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption">{fmtCurrency(log.TotalRevenue)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption">{log.OrderCount ?? '-'}</Typography>
                      </TableCell>
                    </TableRow>
                    {hasDetail && (
                      <TableRow>
                        <TableCell colSpan={11} sx={{ p: 0, borderBottom: 0 }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <DeliveryDetailPanel log={log} />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Sayfa başına"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} / ${count !== -1 ? count : `${to}+`}`
          }
        />
      </TableContainer>
    </Box>
  );
};

// =============================================================================
// Detay paneli (alıcılar + SMTP yanıtı + MessageId + hata mesajı)
// =============================================================================

interface DeliveryDetailPanelProps {
  log: ReportRunLog;
}

const DeliveryDetailPanel: React.FC<DeliveryDetailPanelProps> = ({ log }) => {
  const recipients = log.Recipients ?? [];
  const ccRecipients = log.CcRecipients ?? [];

  return (
    <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
      {log.Subject && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            Konu
          </Typography>
          <Typography variant="body2">{log.Subject}</Typography>
        </Box>
      )}

      {(recipients.length > 0 || ccRecipients.length > 0) && (
        <Box sx={{ mb: 1.5 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <MailOutlineIcon sx={{ fontSize: 16, color: 'primary.main' }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Alıcılar
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            {recipients.map((r) => (
              <Chip
                key={`to-${r}`}
                size="small"
                label={r}
                color="primary"
                variant="outlined"
                sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
              />
            ))}
            {ccRecipients.map((r) => (
              <Chip
                key={`cc-${r}`}
                size="small"
                label={`CC: ${r}`}
                variant="outlined"
                sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
              />
            ))}
          </Stack>
        </Box>
      )}

      {(log.SmtpHost || log.SmtpResponse || log.MessageId) && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <DnsIcon sx={{ fontSize: 16, color: 'info.main' }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              SMTP Teslim Detayı
            </Typography>
          </Stack>
          <Stack spacing={0.75}>
            {log.SmtpHost && (
              <Box>
                <Typography variant="caption" color="text.secondary">Host: </Typography>
                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                  {log.SmtpHost}
                </Typography>
              </Box>
            )}
            {log.MessageId && (
              <Box>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <FingerprintIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    Message-ID:
                  </Typography>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {log.MessageId}
                  </Typography>
                </Stack>
              </Box>
            )}
            {log.SmtpResponse && (
              <Paper
                variant="outlined"
                sx={{
                  p: 1,
                  bgcolor: log.Status === 'success' ? '#f0fdf4' : '#fff7ed',
                  borderColor: log.Status === 'success' ? 'success.light' : 'warning.light',
                }}
              >
                <Typography
                  variant="caption"
                  component="pre"
                  sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', m: 0 }}
                >
                  {log.SmtpResponse}
                </Typography>
              </Paper>
            )}
          </Stack>
        </>
      )}

      {log.ErrorMessage && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="caption" color="error" sx={{ fontWeight: 600 }}>
            Hata mesajı
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              mt: 0.5,
              bgcolor: '#fff5f5',
              borderColor: 'error.light',
              maxHeight: 200,
              overflow: 'auto',
            }}
          >
            <Typography
              variant="caption"
              component="pre"
              sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', m: 0 }}
            >
              {log.ErrorMessage}
            </Typography>
          </Paper>
        </>
      )}
    </Box>
  );
};

// =============================================================================
// KPI kartı
// =============================================================================

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: number;
  tint: string;
}

const StatCard: React.FC<StatCardProps> = ({ icon, label, value, tint }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1.5,
      borderRadius: 2,
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      height: '100%',
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: 1.5,
        background: tint,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Box>
  </Paper>
);

export default RunLogsTab;
